import { createItem, deleteItem, getItem, listItems, updateItem } from './sharepoint';

const LIST = 'plants';

// Columns on the Plants list that store structured data as JSON strings.
// Keeping the translation at the service boundary lets the UI work with plain
// objects/arrays and never touch JSON.parse / stringify.
const JSON_FIELDS = {
  crewShifts: 'CrewShiftsJson',             // [{ shift: 'A', schedule: '...' }]
  liaisons: 'LiaisonsJson',                 // ['Adam Ali', 'Larry Gomoll', ...]
  plantContacts: 'PlantContactsJson',       // [{ name, company, title, email, phone, shift }]
  supplierCodes: 'SupplierCodesJson',       // ['AUTOSYS', 'ABC-CCSI']
  ppe: 'PpeJson',                           // { vestColor, safetyGlasses, ... }  (object, not array)
  // Sequencing Facilities are stored as an array of {name, address, notes}.
  // Legacy records saved a single {name, address, notes} object in this
  // column — decode() handles that by wrapping the object in a one-item
  // array, so older rows keep working transparently.
  sequencingFacilities: 'SequencingFacilityJson',
  vehicles: 'VehiclesJson',                 // [{ make, model, program, status }] — `program` was previously named `modelYear`; decode() back-fills both directions.
  plantClosures: 'PlantClosuresJson',       // [{ startDate: '2026-12-24', endDate: '2027-01-02', reason: 'Christmas' }]
};

function parseJson(raw, fallback) {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function decode(row) {
  if (!row) return row;
  // Legacy rows predate VehiclesJson and have Vehicle/ModelYear/Status at the
  // top level. If we find those but no VehiclesJson, synthesize a one-row
  // vehicles array so the UI still renders correctly. Note ModelYear is
  // mapped into the new `program` field (the column was repurposed —
  // see decode shim below).
  let vehicles = parseJson(row.VehiclesJson, null);
  if (!vehicles) {
    if (row.Vehicle || row.ModelYear || row.Status) {
      vehicles = [
        {
          name: row.Vehicle || '',
          program: row.ModelYear || '',
          status: row.Status || '',
        },
      ];
    } else {
      vehicles = [];
    }
  }
  // Vehicles used to be stored as a single `name` string (e.g. "Ram 1500").
  // The form now captures `make` and `model` separately. Back-fill the new
  // fields from `name` when we're reading a legacy record so the UI has
  // something to show — users can edit a plant to split make/model cleanly.
  // The third column was renamed from `modelYear` to `program`; copy the old
  // value over so plants saved before the rename still show their value
  // (and so any code still reading `modelYear` keeps working until edited).
  vehicles = vehicles.map((v) => {
    if (!v || typeof v !== 'object') return v;
    let next = v;
    const hasSplit = v.make || v.model;
    if (!hasSplit && v.name) {
      next = { ...next, make: '', model: v.name };
    }
    if (next.program == null && next.modelYear != null) {
      next = { ...next, program: next.modelYear };
    }
    return next;
  });
  const plantContacts = parseJson(row.PlantContactsJson, []);
  // Plant Manager used to be a set of dedicated columns
  // (PlantManager / PlantManagerEmail / PlantManagerPhone); it's now
  // captured as one of the OEM Plant Contacts (with title="Plant
  // Manager"). For backward compatibility with callers that still
  // read those fields directly (e.g. TeamMemberDetail's "Plant
  // Manager (OEM)" line), synthesize them from the contacts list
  // whenever the legacy columns are empty. Match is loose — any
  // contact whose title contains "plant manager" — so close-enough
  // titles like "OEM Plant Manager" still resolve.
  const pmContact = (plantContacts || []).find(
    (c) => /plant\s*manager/i.test((c?.title || '').trim()),
  );
  const PlantManager = row.PlantManager || pmContact?.name || '';
  const PlantManagerEmail = row.PlantManagerEmail || pmContact?.email || '';
  const PlantManagerPhone = row.PlantManagerPhone || pmContact?.phone || '';

  return {
    ...row,
    PlantManager,
    PlantManagerEmail,
    PlantManagerPhone,
    crewShifts: parseJson(row.CrewShiftsJson, []),
    liaisons: parseJson(row.LiaisonsJson, []),
    plantContacts,
    supplierCodes: parseJson(row.SupplierCodesJson, []),
    ppe: parseJson(row.PpeJson, {}),
    sequencingFacilities: (() => {
      // Accept either an array (new) or a single object (legacy). For the
      // legacy object form, wrap in a one-item array — but only if it has
      // any actual data, so brand-new records don't come back with a
      // ghost empty entry.
      const parsed = parseJson(row.SequencingFacilityJson, null);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') {
        const hasData = parsed.name || parsed.address || parsed.notes;
        return hasData ? [parsed] : [];
      }
      return [];
    })(),
    vehicles,
    plantClosures: parseJson(row.PlantClosuresJson, []),
  };
}

// Plain (non-JSON) columns this service is allowed to write back. Using an
// allowlist here instead of trying to blacklist SharePoint's ever-expanding
// set of read-only system columns (Modified, Edit, LinkTitle, etc.) — those
// come back in every row read and will blow up the PATCH if they leak in.
// If you add a new writable column to the Plants list, add it here too.
const WRITABLE_PLAIN_COLUMNS = [
  'Title',
  'PlantCode',
  'OEM',
  // Plant classification: OEM / Tier 1 / Tier 2 / Tier 3.
  'PlantType',
  'AddressStreet',
  'AddressStreet2',
  'AddressStreet3',
  'City',
  'State',
  'PostalCode',
  'Country',
  'PlantOrgSheetUrl',
  'Notes',
  // Legacy single-number field — kept readable for plants whose data
  // pre-dates the day-of-week shift split below. The form no longer
  // writes to it.
  'ShiftsPerDay',
  // Day-of-week shift counts (0–4 each). Replace the legacy single
  // ShiftsPerDay number — weekend coverage often differs from
  // weekday coverage so we capture each separately.
  'ShiftsWeekdays',
  'ShiftsSaturdays',
  'ShiftsSundays',
  // Free-form notes that used to live in the per-shift schedule
  // table. Now a single multi-line text box on the Plant form.
  'ShiftNotes',
  // Free-form notes attached to the Vehicles section — typically
  // production-schedule context like "SOP slipped from Mar to Apr"
  // or "Wagoneer holiday shutdown 12/22–1/2". Surfaced underneath
  // the vehicles table on the Plant detail page and PDF.
  'VehicleScheduleNotes',
];

function encode(model) {
  // Build the payload from the allowlist plus the freshly-serialized JSON
  // columns. Everything else (system fields, legacy columns we've retired,
  // stray decoded-row leftovers) is simply ignored.
  const out = {};
  for (const key of WRITABLE_PLAIN_COLUMNS) {
    if (model[key] !== undefined && model[key] !== null) {
      out[key] = model[key];
    }
  }
  out[JSON_FIELDS.crewShifts] = JSON.stringify(model.crewShifts ?? []);
  out[JSON_FIELDS.plantContacts] = JSON.stringify(model.plantContacts ?? []);
  out[JSON_FIELDS.ppe] = JSON.stringify(model.ppe ?? {});
  out[JSON_FIELDS.sequencingFacilities] = JSON.stringify(
    model.sequencingFacilities ?? [],
  );
  // Synthesize a `name` on each vehicle from make + model so readers that
  // still reference v.name (Plants index, Dashboard chip list, PartForm
  // dropdown) keep working after the form captures make/model separately.
  const vehiclesOut = (model.vehicles ?? []).map((v) => {
    const synthesized = [v?.make, v?.model].filter(Boolean).join(' ').trim();
    return { ...v, name: synthesized || v?.name || '' };
  });
  out[JSON_FIELDS.vehicles] = JSON.stringify(vehiclesOut);
  out[JSON_FIELDS.plantClosures] = JSON.stringify(model.plantClosures ?? []);
  return out;
}

export async function listPlants(opts) {
  const rows = await listItems(LIST, {
    orderby: 'fields/Title',
    ...opts,
  });
  return rows.map(decode);
}

export async function getPlant(id) {
  return decode(await getItem(LIST, id));
}

/**
 * Find a plant by its short code (e.g. 'SHAP'). Used by the detail page route.
 * Returns null if no match; caller can handle the "not found" state.
 */
export async function getPlantByCode(plantCode) {
  const rows = await listItems(LIST, {
    filter: `fields/PlantCode eq '${plantCode}'`,
    top: 1,
  });
  return rows.length ? decode(rows[0]) : null;
}

export async function createPlant(model) {
  return decode(await createItem(LIST, encode(model)));
}

export async function updatePlant(id, model) {
  return decode(await updateItem(LIST, id, encode(model)));
}

export async function deletePlant(id) {
  return deleteItem(LIST, id);
}
